import theme from '../../../theme/theme';

export const GEN_COLORS = {
  accent: '#1fb6d8',
  accentLight: '#e6f7fb',
  accentBorder: '#bfe8f2',
  accentText: '#0d7d95',
  ok: '#0f9d76',
  okBg: '#e6f6f1',
  okBorder: '#c4e9dd',
  warn: '#a8631a',
  warnBg: '#fdf3e5',
  warnBorder: '#f0dcbd',
  warnText: '#7a4a12',
  warnBold: '#5e3a0e',
  danger: '#c0392b',
  dangerBg: '#fdf0ee',
  dangerBorder: '#e6c7c2',
  dangerText: '#8f2e22',
  dangerBold: '#7a271d',
  off: '#94a3b8',
  offBg: '#eef1f5',
  gap: '#e0a83d',
  border: '#d8dee5',
  borderStrong: '#c3cbd6',
  primaryText: '#335c83',
  primaryBg: '#eaf0f6',
  primaryBorder: '#cfdce8',
} as const;

export type ChannelType = 'general' | 'pilote';

export const CHANNEL_STYLES: Record<
  ChannelType,
  { stripColor: string; tagBg: string; tagBorder: string; tagColor: string }
> = {
  general: {
    stripColor: theme.palette.primary.main,
    tagBg: GEN_COLORS.primaryBg,
    tagBorder: GEN_COLORS.primaryBorder,
    tagColor: GEN_COLORS.primaryText,
  },
  pilote: {
    stripColor: GEN_COLORS.accent,
    tagBg: GEN_COLORS.accentLight,
    tagBorder: GEN_COLORS.accentBorder,
    tagColor: GEN_COLORS.accentText,
  },
};

export type DiffusionStatus = 'ok' | 'pending' | 'gap' | 'never' | 'fail' | 'none';

export const STATUS_COLORS: Record<
  DiffusionStatus,
  { bg: string; border: string; color: string }
> = {
  ok: { bg: GEN_COLORS.okBg, border: GEN_COLORS.okBorder, color: GEN_COLORS.ok },
  pending: {
    bg: GEN_COLORS.accentLight,
    border: GEN_COLORS.accentBorder,
    color: GEN_COLORS.primaryText,
  },
  gap: { bg: GEN_COLORS.warnBg, border: GEN_COLORS.warnBorder, color: GEN_COLORS.warn },
  never: { bg: GEN_COLORS.offBg, border: GEN_COLORS.border, color: GEN_COLORS.off },
  fail: { bg: GEN_COLORS.dangerBg, border: GEN_COLORS.dangerBorder, color: GEN_COLORS.danger },
  none: { bg: GEN_COLORS.offBg, border: GEN_COLORS.border, color: GEN_COLORS.off },
};

export const BAR_COLORS: Record<DiffusionStatus, string> = {
  ok: GEN_COLORS.ok,
  pending: GEN_COLORS.accent,
  gap: GEN_COLORS.gap,
  fail: GEN_COLORS.danger,
  never: GEN_COLORS.off,
  none: GEN_COLORS.off,
};

export const CALLOUT_STYLES: Record<
  'warn' | 'danger',
  { bg: string; border: string; color: string; bold: string }
> = {
  warn: {
    bg: GEN_COLORS.warnBg,
    border: GEN_COLORS.warnBorder,
    color: GEN_COLORS.warnText,
    bold: GEN_COLORS.warnBold,
  },
  danger: {
    bg: GEN_COLORS.dangerBg,
    border: GEN_COLORS.dangerBorder,
    color: GEN_COLORS.dangerText,
    bold: GEN_COLORS.dangerBold,
  },
};

export const MONOSPACE_FONT =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

export const GEN_PAGE = {
  contentPadding: '22px 28px',
  bannerPadding: '13px 18px',
  bannerMarginBottom: '12px',
  bannerGap: '14px',
  bannerHeaderFontSize: '11px',
  bannerNumberSize: 22,
  bannerLabelSize: '12.5px',
  bannerSepSize: '16px',
  bannerTotalFontSize: '11.5px',
  bannerBarMt: '2px',
  bannerNoticeGap: '10px',
  bannerNoticePadding: '11px 13px',
  bannerNoticeLineHeight: 1.6,
  bannerNoticeMarginBottom: '16px',
  gridGap: '18px',
} as const;

export const GEN_CARD = {
  font: {
    sizeTag: '10.5px',
    sizeBody: 12,
    sizeSmall: '11.5px',
    sizeVersion: 19,
    sizeKpi: 17,
    lineHeightTight: 1.1,
    lineHeightTitle: 1.2,
    lineHeightNormal: 1.5,
    lineHeightRelaxed: 1.6,
    letterSpacingLabel: '0.6px',
    letterSpacingVersion: '-0.2px',
  },
  spacing: {
    headerPadding: '14px 18px 12px',
    bodyPadding: '16px 18px 4px',
    emptyPadding: '34px 18px 30px',
    footerPadding: '14px 18px',
    kpiPadding: '10px 12px',
    calloutPadding: '11px 13px',
    chipPx: '9px',
    buttonPx: '13px',
    gap: '10px',
    gapSm: '6px',
    section: '16px',
    calloutMt: '14px',
    scopeMt: '4px',
    emptyIconMb: '12px',
    emptyDescMt: '6px',
    versionLabelMb: '6px',
    publishedMt: '10px',
    diffusionPt: '12px',
    legendMt: '11px',
    alertInset: '18px',
    calloutIconMt: '1px',
    kpiLabelMt: '3px',
  },
  size: {
    barHeight: 7,
    emptyIconBox: 38,
    emptyDescMaxWidth: 290,
    buttonHeight: 34,
    calloutIconBox: 15,
    stripWidth: 3,
    iconEmpty: 19,
    iconCallout: 15,
    iconUpload: 14,
  },
} as const;
